/**
 * Skills Service — F-SKILL-1 Packet B1 + B2
 *
 * HTTP helpers for the /api/v1/agent-skills endpoints.
 * Uses apiFetch (via fetchJson/mutate) — never raw fetch().
 *
 * Types align to the server-side RorosSkillSummaryDTO / CreateSkillPayload
 * (see server/modules/agent-skills/agent-skills.types.ts).
 *
 * B2 additions: getSkill, updateSkill, deleteSkill,
 *               listAttachments, uploadAttachment, removeAttachment,
 *               listVersions, getVersion, restoreVersion.
 */

import { fetchJson, mutate } from './base-roros.service';
import { apiFetch } from '@/lib/api';
import type {
  SkillCategory,
  CreateSkillPayload,
  UpdateSkillPayload,
  SkillMatrixEntry,
  RorosSkillDTO,
  RorosSkillAttachmentDTO,
  RorosSkillVersionSummaryDTO,
  RorosSkillVersionDTO,
} from '@/types/roros/skills';

// ─── Re-export for convenience ────────────────────────────────────────────────
export type { UpdateSkillPayload };

// ─── Paginated list result ────────────────────────────────────────────────────

export interface RorosSkillListItem {
  id: string;
  orgId: string;
  name: string;
  displayName: string;
  description: string;
  category: SkillCategory;
  isSystem: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SkillListMeta {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface SkillListResult {
  data: RorosSkillListItem[];
  meta: SkillListMeta;
}

export interface ListSkillsParams {
  search?: string;
  category?: SkillCategory;
  isSystem?: boolean;
  isActive?: boolean;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// ─── B2: Attachment upload result ─────────────────────────────────────────────
export type { RorosSkillDTO, RorosSkillAttachmentDTO, RorosSkillVersionSummaryDTO, RorosSkillVersionDTO };

// ─── Service ──────────────────────────────────────────────────────────────────

function buildQuery(params: ListSkillsParams): string {
  const q = new URLSearchParams();
  if (params.search) q.set('search', params.search);
  if (params.category) q.set('category', params.category);
  if (params.isSystem !== undefined) q.set('isSystem', String(params.isSystem));
  if (params.isActive !== undefined) q.set('isActive', String(params.isActive));
  if (params.page !== undefined) q.set('page', String(params.page));
  if (params.pageSize !== undefined) q.set('pageSize', String(params.pageSize));
  if (params.sortBy) q.set('sortBy', params.sortBy);
  if (params.sortOrder) q.set('sortOrder', params.sortOrder);
  const str = q.toString();
  return str ? `?${str}` : '';
}

/**
 * NOTE: fetchJson unwraps `response.data` automatically.
 * The list endpoint returns `{ success, data: RorosSkillSummaryDTO[], meta }`.
 * We cast to SkillListResult because fetchJson returns `json.data` which
 * is the `data` array for list endpoints — but the list endpoint wraps
 * both data + meta at the top level alongside `success`.
 * We use a raw apiFetch call here to get both.
 */

async function listSkills(params: ListSkillsParams = {}): Promise<SkillListResult> {
  const res = await apiFetch(`/api/v1/agent-skills${buildQuery(params)}`);
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
  }
  const json = await res.json();
  return {
    data: json.data as RorosSkillListItem[],
    meta: json.meta as SkillListMeta,
  };
}

async function getMatrix(): Promise<SkillMatrixEntry[]> {
  return fetchJson<SkillMatrixEntry[]>('/api/v1/agent-skills/matrix');
}

async function createSkill(payload: CreateSkillPayload): Promise<RorosSkillListItem> {
  return mutate<RorosSkillListItem>('/api/v1/agent-skills', 'POST', {
    name: payload.name,
    display_name: payload.display_name,
    description: payload.description,
    content: payload.content,
    category: payload.category,
  });
}

// ─── B2: Get single skill detail ──────────────────────────────────────────────

async function getSkill(id: string): Promise<RorosSkillDTO> {
  return fetchJson<RorosSkillDTO>(`/api/v1/agent-skills/${id}`);
}

// ─── B2: Update a custom skill ────────────────────────────────────────────────

async function updateSkill(id: string, payload: UpdateSkillPayload): Promise<RorosSkillDTO> {
  return mutate<RorosSkillDTO>(`/api/v1/agent-skills/${id}`, 'PUT', payload);
}

// ─── B2: Delete a custom skill ────────────────────────────────────────────────

async function deleteSkill(id: string): Promise<void> {
  await mutate<unknown>(`/api/v1/agent-skills/${id}`, 'DELETE');
}

// ─── B2: List reference-file attachments ──────────────────────────────────────

async function listAttachments(skillId: string): Promise<RorosSkillAttachmentDTO[]> {
  return fetchJson<RorosSkillAttachmentDTO[]>(`/api/v1/agent-skills/${skillId}/attachments`);
}

// ─── B2: Upload a reference file (multipart) ──────────────────────────────────

async function uploadAttachment(skillId: string, file: File): Promise<RorosSkillAttachmentDTO> {
  const form = new FormData();
  form.append('file', file);

  const res = await apiFetch(`/api/v1/agent-skills/${skillId}/attachments`, {
    method: 'POST',
    body: form,
    // Do NOT set Content-Type — browser sets it with boundary automatically
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const code = body?.error?.code ?? 'UPLOAD_FAILED';
    const message = body?.error?.message ?? `Upload failed (${res.status})`;
    const err = Object.assign(new Error(message), { status: res.status, code });
    throw err;
  }
  const json = await res.json();
  return json.data as RorosSkillAttachmentDTO;
}

// ─── B2: Remove a reference file attachment ───────────────────────────────────

async function removeAttachment(skillId: string, linkId: string): Promise<void> {
  await mutate<unknown>(`/api/v1/agent-skills/${skillId}/attachments/${linkId}`, 'DELETE');
}

// ─── B2: List version summaries ───────────────────────────────────────────────

async function listVersions(skillId: string): Promise<RorosSkillVersionSummaryDTO[]> {
  return fetchJson<RorosSkillVersionSummaryDTO[]>(`/api/v1/agent-skills/${skillId}/versions`);
}

// ─── B2: Get full snapshot of a specific version ──────────────────────────────

async function getVersion(skillId: string, versionId: string): Promise<RorosSkillVersionDTO> {
  return fetchJson<RorosSkillVersionDTO>(`/api/v1/agent-skills/${skillId}/versions/${versionId}`);
}

// ─── B2: Restore a prior version ─────────────────────────────────────────────

async function restoreVersion(skillId: string, versionId: string): Promise<RorosSkillDTO> {
  return mutate<RorosSkillDTO>(`/api/v1/agent-skills/${skillId}/versions/${versionId}/restore`, 'POST');
}

// ─── B3: Assign a skill to an agent definition ───────────────────────────────

async function assignSkill(skillId: string, agentDefinitionId: string): Promise<void> {
  await mutate<unknown>(`/api/v1/agent-skills/${skillId}/assign`, 'POST', { agentDefinitionId });
}

// ─── B3: Unassign a skill from an agent definition ───────────────────────────

async function unassignSkill(skillId: string, agentDefinitionId: string): Promise<void> {
  await mutate<unknown>(`/api/v1/agent-skills/${skillId}/unassign/${agentDefinitionId}`, 'DELETE');
}

// ─── Export ───────────────────────────────────────────────────────────────────

export const SkillsService = {
  list: listSkills,
  getMatrix,
  create: createSkill,
  // B2
  get: getSkill,
  update: updateSkill,
  delete: deleteSkill,
  listAttachments,
  // B3
  assignSkill,
  unassignSkill,
  uploadAttachment,
  removeAttachment,
  listVersions,
  getVersion,
  restoreVersion,
};
